import { DomainHTTP } from '@/http'
import { DomainDTO } from '@/dtos'
import { DomainModel } from '@/models'
import {
  DomainAddToCartResponseData,
  DomainCheckResponseData
} from '@/core/ryanada'

/**
 * The View-Model that is used for Domain Configuration
 */
export class DomainViewModel {
  private models: DomainModel[] = []
  private httpProvider: DomainHTTP

  constructor(httpProvider: DomainHTTP) {
    this.httpProvider = httpProvider
  }

  /**
   * Sets a new httpProvider
   * @param provider
   */
  public setHTTPProvider(provider: DomainHTTP): void {
    this.httpProvider = provider
  }

  /**
   * Adds Domain Models to View Model
   * @param model
   */
  public addModel(model: DomainModel): void {
    this.models.push(model)
  }

  /**
   * Make a DomainModel from data embedded in html el
   * @param widgetEl
   * @param token
   * @returns
   */
  makeModel(widgetEl: HTMLElement, token: string): DomainModel {
    const cartIndexEl = widgetEl.querySelector<HTMLInputElement>(
      'input[name=cartIndex]'
    )
    const domainNameEl =
      widgetEl.querySelector<HTMLInputElement>('input[name=domain]')
    const periodEl = widgetEl.querySelector<HTMLInputElement>(
      'select[name^=period]'
    )
    const idProtectionEl = widgetEl.querySelector<HTMLInputElement>(
      'input[name^=idprotection]'
    )
    const dnsManagementEl = widgetEl.querySelector<HTMLInputElement>(
      'input[name^=dnsmanagement]'
    )
    const emailForwardingEl = widgetEl.querySelector<HTMLInputElement>(
      'input[name^=emailforwarding]'
    )

    const cartIndex = cartIndexEl?.value
    const domainName = domainNameEl?.value
    const period = periodEl?.value

    if (!cartIndex) {
      throw new Error('DomainViewModel: Cart Index is not set')
    }

    if (!domainName) {
      throw new Error('DomainViewModel: Domain Name is not set')
    }

    if (!period) {
      throw new Error('DomainViewModel: Period is not set')
    }

    if (!idProtectionEl) {
      throw new Error('DomainViewModel: id protection is not set')
    }

    if (!dnsManagementEl) {
      throw new Error('DomainViewModel: dns management is not set')
    }

    if (!emailForwardingEl) {
      throw new Error('DomainViewModel: email forwarding is not set')
    }

    const idProtection = idProtectionEl.checked
    const dnsManagement = dnsManagementEl.checked
    const emailForwarding = emailForwardingEl.checked

    const dto = new DomainDTO(
      cartIndex,
      domainName,
      Number.parseInt(period),
      idProtection,
      dnsManagement,
      emailForwarding
    )

    return new DomainModel(dto, token)
  }

  /**
   * Check if domain already in cart
   * @param domain
   * @returns
   */
  public hasDomain(domainName: string): boolean {
    return (
      this.models.find((model) => model.getName() === domainName) !== undefined
    )
  }

  /**
   * Configure/update domains already in cart
   * @param domain
   * @returns
   */
  public addToCart(domain: DomainModel): Promise<boolean> {
    const models = [...this.models].filter(
      (model) => model.cartIndex !== domain.cartIndex
    )
    models.push(domain)
    return this.httpProvider.post(models)
  }

  /**
   * Update Period
   * @param domain
   * @returns
   */
  public updatePeriod(domain: DomainModel): Promise<boolean> {
    return this.httpProvider.updatePeriod(domain)
  }

  /**
   * Add a single domain to cart
   * @param formData
   * @returns
   */
  public addToCartSingle(
    formData: FormData
  ): Promise<DomainAddToCartResponseData> {
    return this.httpProvider.addToCart(formData)
  }

  /**
   * Get information about domain
   * @param formData
   * @returns
   */
  public checkDomain(formData: FormData): Promise<DomainCheckResponseData> {
    return this.httpProvider.checkDomain(formData)
  }
}
